import { Temporal, useLocale } from "@hermes/intl";
// TODO! Remove after rewriting in react-aria
import { enUS as en, hu, kk, ro, ru } from "date-fns/locale";
import { type MouseEventHandler, type SVGProps, useState } from "react";
import { DayPicker, type Matcher } from "react-day-picker";

import { icons } from "..";
import { cssFns } from "../css";
import { colors, usePrincipalColors } from "../theme";

const PickerRightButtonIcon = (props: SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="0.65"
        y="0.65"
        width="26.7"
        height="26.7"
        rx="13.35"
        stroke="#5194C1"
        strokeWidth="1.3"
      />
      <path
        d="M12 9L17 14L12 19"
        stroke="#5194C1"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const PickerLeftButtonIcon = (props: SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="-0.65"
        y="0.65"
        width="26.7"
        height="26.7"
        rx="13.35"
        transform="matrix(-1 0 0 1 26.7 0)"
        stroke="#5194C1"
        strokeWidth="1.3"
      />
      <path
        d="M16 9L11 14L16 19"
        stroke="#5194C1"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

//TODO Remove in next version
const CustomNav = ({
  previousMonth,
  onNextClick,
  onPreviousClick,
  disabled,
}: {
  previousMonth?: Date;
  nextMonth?: Date;
  onNextClick?: MouseEventHandler<HTMLButtonElement>;
  onPreviousClick?: MouseEventHandler<HTMLButtonElement>;
  minDate?: Temporal.PlainDate;
  disabled?: Matcher | Matcher[] | undefined;
}) => {
  const getBeforeDate = (): Date | undefined => {
    if (Array.isArray(disabled)) {
      const beforeMatcher = disabled.find(
        (d) => typeof d === "object" && "before" in d,
      ) as { before?: Date };
      return beforeMatcher.before;
    }
    if (typeof disabled === "object" && "before" in disabled) {
      return disabled.before;
    }
    return undefined;
  };
  const beforeDate = getBeforeDate();

  const isPrevDisabled =
    !previousMonth ||
    (beforeDate &&
      Temporal.PlainDate.compare(
        Temporal.PlainDate.from(previousMonth.toISOString().slice(0, 10)).add({
          days: 2,
        }),
        Temporal.PlainDate.from(beforeDate.toISOString().slice(0, 10)).with({
          day: 1,
        }),
      ) <= 0);

  return (
    <div
      className="rdp-nav"
      css={[cssFns.paddingBlock(20), { height: 50 }]}
      role="navigation"
    >
      <button
        type="button"
        className="rdp-button_previous"
        css={{
          opacity: "1 !important",
        }}
        disabled={isPrevDisabled}
        onClick={onPreviousClick}
      >
        {isPrevDisabled ? (
          <icons.DesktopArrowLeftDisabled />
        ) : (
          <PickerLeftButtonIcon />
        )}
      </button>
      <button type="button" className="rdp-button_next" onClick={onNextClick}>
        <PickerRightButtonIcon />
      </button>
    </div>
  );
};

export const Calendar = ({
  value,
  onChange,
  disabled,
  maxDate,
  minDate,
}: {
  value?: Temporal.PlainDate;
  onChange?: (value?: Temporal.PlainDate) => void;
  disabled?: Matcher | Matcher[] | undefined;
  maxDate?: Temporal.PlainDate;
  minDate?: Temporal.PlainDate;
}) => {
  const [locale] = useLocale();
  const principalColors = usePrincipalColors();

  const date = value ? new Date(value.toString()) : undefined;

  const [displayMonth, setDisplayMonth] = useState(() =>
    value ? new Date(value.toString()) : new Date(),
  );

  const beforeMatcher: Matcher | undefined = minDate
    ? { before: new Date(minDate.toString()) }
    : undefined;

  const afterMatcher: Matcher | undefined = maxDate
    ? { after: new Date(maxDate.toString()) }
    : undefined;

  const hiddenDates: Matcher[] | undefined = [
    beforeMatcher,
    afterMatcher,
  ].filter(Boolean) as Matcher[];

  return (
    <DayPicker
      autoFocus={true}
      month={displayMonth}
      onMonthChange={setDisplayMonth}
      fixedWeeks
      captionLayout="label"
      showOutsideDays
      modifiersStyles={{
        today: {
          color: colors.principal.ebblue,
          opacity: 1,
          border: "none",
        },
        selected: {
          backgroundColor: colors.principal.ebblue,
          color: colors.principal.white,
        },
        outside: {
          opacity: 0.6,
        },
      }}
      css={[
        cssFns.typo({ level: "body-1", weight: "regular" }),
        {
          "*": [cssFns.typo({ level: "body-1", weight: "regular" })],
          th: [
            cssFns.padding(0),
            {
              opacity: 1,
              color: principalColors.gs2,
            },
          ],
          "*:focus-visible": {
            outlineColor: principalColors.ebblue,
            outlineStyle: "solid",
            outlineWidth: 2,
            borderRadius: 4,
          },
          ".rdp-nav": {
            left: "0px",
            top: "5px",
          },
          ".rdp-button_previous, .rdp-button_next": {
            position: "absolute",
          },
          ".rdp-button_previous": {
            left: 10,
            width: "50px",
            height: "50px",
          },
          ".rdp-button_next": {
            right: 10,
            width: "50px",
            height: "50px",
          },
          ".rdp-months": {
            maxWidth: "none",
            "@media(max-width:1024px)": {
              display: "block",
              table: {
                margin: "auto",
              },
            },
          },
          ".rdp-month_caption": [
            cssFns.padding("20px", "0px"),
            {
              justifyContent: "center",
              height: "auto",
            },
          ],
          ".rdp-caption_label, [role='columnheader']": [
            cssFns.typo({ level: "body-1", weight: "semibold" }),
            {
              color: principalColors.gs2,
              textTransform: "capitalize",
            },
          ],
          ".rdp-month_wrapper": {
            flexGrow: 1,
            paddingBlockEnd: "16px",
          },
          ".rdp-selected, .rdp-today, .rdp-day": [
            cssFns.typo({ level: "body-1", weight: "regular" }),
            {
              color: principalColors.gs2,
            },
          ],
          ".rdp-day": [
            {
              width: 48,
              height: 48,
              "&_button": {
                width: 46,
                height: 46,
              },
            },
          ],
          ".rdp-day_button": {
            display: "inline-block !important",
            opacity: 1,
            ":disabled": {
              opacity: 0.3,
            },
          },

          ".rdp-selected": [
            cssFns.border({ radius: 6, width: "0px" }),
            {
              color: principalColors.white,
              position: "relative",
              zIndex: 1,
              button: {
                borderColor: "transparent",
              },
              ":after": {
                backgroundColor: principalColors.ebblue,
                content: "' '",
                zIndex: -1,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 40,
                height: 40,
                borderRadius: 4,
              },
            },
          ],
        },
      ]}
      mode="single"
      disabled={disabled}
      locale={
        {
          en,
          hu,
          kk,
          ro,
          ru,
        }[locale.baseName]
      }
      components={{
        Nav: (props) => <CustomNav {...props} disabled={disabled} />,
      }}
      hidden={hiddenDates}
      selected={date}
      onSelect={(date) => {
        if (date) {
          const pre = (v: number) => (v < 10 ? "0" + v : v);
          const plainDate = `${date.getFullYear()}-${pre(date.getMonth() + 1)}-${pre(date.getDate())}`;

          onChange?.(Temporal.PlainDate.from(plainDate));
        } else {
          onChange?.(undefined);
        }
      }}
    />
  );
};
